import { IonFooter, IonHeader, IonPage, IonRadio, IonRadioGroup, useIonRouter } from "@ionic/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "@tanstack/react-form";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { usePostHog } from "posthog-js/react";
import { z } from "zod";
import SplashIcon from "@/assets/splash_icon.svg";
import { FormTextarea } from "@/components/Form/Textarea";
import { getErrorText } from "@/components/Form/utils";
import { Button } from "@/components/Global/Button";
import { Content } from "@/components/Global/Content";
import { Toolbar } from "@/components/Global/Toolbar";
import { backendService } from "@/lib/backend";
import { type FriendCheckQuestionTree, type SubmitFriendcheckDto } from "@/lib/backend/api";
import { useExtractedMutation } from "@/lib/backend/utils/hooks";
import { getAnswer } from "@/lib/functions/answer";
import { useKeyboard } from "@/lib/hooks/useKeyboard";
import { queryOptions_publicFriendCheck } from "@/lib/query/functions/friend-check/public";
import { Routes } from "@/lib/router";
import { useFriendCheckStore } from "@/lib/store/friend-check";
import { cx } from "@/lib/style/cva.config";

type FlattenedQuestion = FriendCheckQuestionTree & { initialQuestion?: FriendCheckQuestionTree };

export function SharedFriendCheckQuestions() {
	const { t, i18n } = useTranslation();
	const posthog = usePostHog();
	const router = useIonRouter();
	const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
	const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
	const state = useFriendCheckStore();
	const { isOpen } = useKeyboard();
	const bottomRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!state.friendId || !state.friendHash) {
			router.push(Routes.Dashboard(), "none", "replace");
		}
	}, [state]);

	const query = useQuery({
		...queryOptions_publicFriendCheck({ friendId: state.friendId, friendHash: state.friendHash }),
		enabled: !!state.friendId && !!state.friendHash,
	});

	const friendCheck = query.data;

	function flattenQuestions(question: FriendCheckQuestionTree | null): Array<FlattenedQuestion> {
		if (!question || !question.followUpQuestion) {
			return [];
		}

		return [
			{ ...question.followUpQuestion, initialQuestion: question },
			...flattenQuestions(question.followUpQuestion),
		];
	}

	function groupQuestions(flattenedQuestions: Array<FlattenedQuestion>) {
		return flattenedQuestions.reduce<Array<Array<FlattenedQuestion>>>((groups, question) => {
			if (!question.initialQuestion) {
				groups.push([question]);
			} else {
				groups[groups.length - 1].push(question);
			}
			return groups;
		}, []);
	}

	const questionGroups = groupQuestions(
		friendCheck?.questions?.flatMap<
			FriendCheckQuestionTree & { initialQuestion?: FriendCheckQuestionTree }
		>((question) => [question, ...flattenQuestions(question)]) ?? [],
	);

	function getFormIndex(groupIndex: number, questionIndex: number): number {
		const previousGroups = questionGroups
			.slice(0, groupIndex)
			.reduce((acc, group) => acc + group.length, 0);

		return previousGroups + questionIndex;
	}

	const mutation = useExtractedMutation({
		mutationKey: ["friend-check", "create"],
		mutationFn: ({
			friendId,
			friendHash,
			...data
		}: { friendId: string; friendHash: string } & SubmitFriendcheckDto) =>
			backendService.friendcheck.friendcheckControllerPostPublicFriendCheck(
				friendId,
				friendHash,
				data,
			),
	});

	const isLastQuestion = !friendCheck
		? false
		: currentGroupIndex >= questionGroups.length - 1 &&
			currentQuestionIndex >= questionGroups[currentGroupIndex].length - 1;

	const form = useForm({
		defaultValues: { answers: [] as Array<string | string[]> },
		onSubmit: ({ value }) => {
			if (!state.submitted) {
				mutation.mutate(
					{
						friendId: state.friendId,
						friendHash: state.friendHash,
						name: state.name,
						relation: state.relation,
						email: state.acceptsEmail ? state.email : undefined,
						questions: questionGroups.flat().map((question, index) => ({
							questionId: question.id,
							answer: getAnswer(value.answers[index], { language: i18n.language }),
						})),
					},
					{
						onSuccess: (data) => {
							useFriendCheckStore.setState((state) => {
								state.submitted = true;
								state.result = data;
							});
							posthog.capture("Friend Check Submitted", { friendId: state.friendId });
							router.push(Routes.SharedFriendCheckThankYou());
						},
					},
				);
			} else {
				router.push(Routes.SharedFriendCheckThankYou());
			}
		},
	});

	return (
		<IonPage>
			<form
				className="flex flex-1 flex-col gap-4"
				style={{ minHeight: "calc(calc(100vh - var(--offset-top)) - 200px)" }}
				onSubmit={(event) => {
					event.preventDefault();
					event.stopPropagation();
					form.handleSubmit();
				}}
			>
				<IonHeader className="ion-no-border">
					<Toolbar className="ion-p-2 ion-pb-2 ion-pt-6 ion-min-h-16 plt-desktop:sm:ion-px-[max(calc(calc(100vw-640px)/2),--spacing(4))]">
						<div
							slot="start"
							className="plt-native:hidden flex min-w-0 items-center justify-start gap-3"
						>
							<img
								src={SplashIcon}
								className="drag-none -mx-2 -mt-[1.125rem] -mb-4 size-12 min-h-12 min-w-12 select-none"
							/>
							<span className="text-brown-700 truncate text-start text-base font-semibold">
								{t("seo.title")}
							</span>
						</div>
					</Toolbar>
				</IonHeader>

				<Content scrollY>
					<div className="relative isolate flex-1">
						{questionGroups.map((questions, groupIndex) => {
							return (
								<motion.div
									className="absolute flex min-h-full w-full flex-col gap-4"
									key={groupIndex}
									initial={
										groupIndex === currentGroupIndex
											? "current"
											: groupIndex > currentGroupIndex
												? "next"
												: "previous"
									}
									animate={
										groupIndex === currentGroupIndex
											? "current"
											: groupIndex > currentGroupIndex
												? "next"
												: "previous"
									}
									variants={{
										current: {
											opacity: 1,
											x: 0,
											scale: 1,
											rotate: 0,
											zIndex: 0,
										},
										previous: {
											opacity: 0,
											x: "-75%",
											scale: 0.75,
											rotate: -3,
											zIndex: -1,
										},
										next: {
											opacity: 0,
											x: "75%",
											scale: 0.75,
											rotate: 3,
											zIndex: 1,
										},
									}}
									transition={{ ease: "easeInOut", duration: 0.4, bounce: 0 }}
								>
									<AnimatePresence>
										{questions.map((question, questionIndex) => {
											const isVisible = questionIndex <= currentQuestionIndex;

											if (!isVisible) {
												return null;
											}

											return (
												<motion.div
													key={question.id}
													className={cx("flex min-h-fit flex-col gap-4 rounded-2xl bg-white p-3")}
													initial={questionIndex === 0 ? "current" : "next"}
													animate={
														questionIndex === currentQuestionIndex
															? "current"
															: questionIndex > currentQuestionIndex
																? "next"
																: "previous"
													}
													variants={{
														current: {
															opacity: 1,
															height: "100%",
															flexGrow: 1,
															scale: 1,
														},
														next: {
															opacity: 0,
															height: "0%",
															scale: 0.25,
															flexGrow: 0,
														},
														previous: {
															height: "0%",
															flexGrow: 0,
														},
													}}
													transition={{
														duration: 0.2,
														type: "tween",
														ease: "easeInOut",
														bounce: 0,
													}}
													onAnimationComplete={() => {
														if (questionIndex === questions.length - 1) {
															bottomRef.current?.scrollIntoView({ behavior: "smooth" });
														}
													}}
												>
													<strong
														data-mask-text
														className="text-brown-600 text-base font-semibold text-pretty"
													>
														{question.question}
													</strong>
													{(function () {
														switch (question.questionType) {
															case "MULTIPLE_CHOICE":
																return (
																	<form.Field
																		name={`answers[${getFormIndex(groupIndex, questionIndex)}]`}
																		validators={{
																			onSubmit: z
																				.array(z.string())
																				.min(1, t("fields.options.errors.min", { count: 1 })),
																		}}
																		children={(field) => (
																			<IonRadioGroup
																				data-attr="page.share.friend-check.questions.answer.radio-group"
																				name={field.name}
																				value={field.state.value}
																				onIonChange={(event) => {
																					field.handleChange(String(event.detail.value));
																				}}
																				onBlur={field.handleBlur}
																				className={cx(
																					"flex flex-col gap-2",
																					field.state.meta.isTouched && "ion-touched",
																					!!field.state.meta.errors &&
																						field.state.meta.errors.length > 0 &&
																						"ion-invalid",
																				)}
																			>
																				{question.options.map((option, index) => (
																					<IonRadio
																						key={index}
																						data-attr="page.share.friend-check.questions.answer.radio"
																						value={option}
																						labelPlacement="end"
																						justify="start"
																						disabled={
																							field.state.meta.isValidating ||
																							field.form.state.isSubmitting
																						}
																						className="bg-brown-200 text-brown-700 rounded-xl p-3 text-base font-normal"
																					>
																						{option}
																					</IonRadio>
																				))}
																				{!!field.state.meta.errors &&
																					!!field.state.meta.errors.length && (
																						<span className="text-danger-500 text-sm font-semibold">
																							{getErrorText({
																								language: i18n.language,
																								errors: field.state.meta.errors,
																							})}
																						</span>
																					)}
																			</IonRadioGroup>
																		)}
																	/>
																);
															case "NUMBER":
															case "TEXT":
															case "EMAIL":
															case "DATA":
															default:
																return (
																	<>
																		<form.Field
																			name={`answers[${getFormIndex(groupIndex, questionIndex)}]`}
																			validators={{
																				onSubmit: z
																					.string({
																						message: t("fields.answer.errors.min", {
																							count: 3,
																						}),
																					})
																					.min(3, t("fields.answer.errors.min", { count: 3 })),
																			}}
																			children={(field) => (
																				<>
																					{questionIndex === currentQuestionIndex ? (
																						<>
																							<FormTextarea
																								data-attr="page.share.friend-check.questions.answer"
																								data-question-index={questionIndex}
																								field={field}
																								placeholder={t("fields.answer.placeholder")}
																								inputMode="text"
																								autocapitalize="sentences"
																								onKeyDown={async (event) => {
																									if (event.metaKey && event.code === "Enter") {
																										field.handleChange(
																											String(
																												(
																													event.target as unknown as {
																														value: string;
																													}
																												)?.value ?? "",
																											),
																										);

																										if (
																											questionIndex === questions.length - 1 &&
																											groupIndex === questionGroups.length - 1
																										) {
																											form.handleSubmit();
																											return;
																										}

																										const result = await field.validate("submit");

																										if (result.length === 0) {
																											const currentGroup =
																												questionGroups[currentGroupIndex];

																											if (
																												currentQuestionIndex ===
																												currentGroup.length - 1
																											) {
																												setCurrentQuestionIndex(0);
																												setCurrentGroupIndex(
																													(current) => ++current,
																												);
																											} else {
																												setCurrentQuestionIndex(
																													(current) => ++current,
																												);
																											}
																										}
																									}
																								}}
																								className={cx(
																									"text-brown-700 [&:not(.ion-invalid)_.native-wrapper]:border-brown-200! flex h-full min-h-80! flex-1 flex-col",
																								)}
																							/>
																						</>
																					) : (
																						<div
																							onClick={() => {
																								if (questionIndex !== currentQuestionIndex) {
																									setCurrentQuestionIndex(questionIndex);
																								}
																							}}
																							className="text-brown-700 border-brown-200 bg-brown-100 truncate rounded-2xl border-2 p-3 whitespace-pre"
																							data-mask-text
																						>
																							{field.state.value}
																						</div>
																					)}
																				</>
																			)}
																		/>
																	</>
																);
														}
													})()}
												</motion.div>
											);
										})}
									</AnimatePresence>
								</motion.div>
							);
						})}
						<div ref={bottomRef} />
					</div>
				</Content>

				<IonFooter className="plt-desktop:sm:px-[max(calc(calc(100vw-640px)/2),--spacing(4))]">
					<div data-keyboard={isOpen} className="flex flex-col items-center gap-4 overflow-hidden">
						<div className="flex w-full items-center justify-between gap-4 *:flex-1 *:rounded-lg *:text-lg *:font-bold">
							<Button
								data-attr="page.share.friend-check.questions.previous"
								disabled={currentGroupIndex === 0 && currentQuestionIndex === 0}
								onClick={() => {
									const key =
										`answers[${getFormIndex(currentGroupIndex, currentQuestionIndex)}]` as const;
									const value = form.getFieldValue(key);

									if (!value) {
										form.setFieldValue(key, "");
									}

									if (currentGroupIndex > 0 || currentQuestionIndex > 0) {
										if (currentQuestionIndex === 0) {
											setCurrentGroupIndex((current) => --current);
											setCurrentQuestionIndex(questionGroups[currentGroupIndex].length - 1);
										} else {
											setCurrentQuestionIndex((current) => --current);
										}
									} else {
										if (router.canGoBack()) {
											router.goBack();
										} else {
											router.push(Routes.SharedFriendCheck());
										}
									}
								}}
								variant="secondary"
							>
								<motion.span initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }}>
									{t("previous")}
								</motion.span>
							</Button>
							<form.Subscribe
								selector={(state) => [state.isSubmitting]}
								children={([isSubmitting]) => (
									<Button
										data-attr="page.share.friend-check.questions.next"
										type={isLastQuestion ? "submit" : "button"}
										className="relative *:absolute"
										disabled={isSubmitting || mutation.isPending}
										onClick={
											!isLastQuestion
												? async () => {
														const key =
															`answers[${getFormIndex(currentGroupIndex, currentQuestionIndex)}]` as const;

														const value = form.getFieldValue(key);

														if (!value) {
															form.setFieldValue(key, "");
														}

														const result = await form.validateField(key, "submit");

														if (result.length !== 0) {
															return;
														}

														if (form.getFieldValue("answers").length - 1 === currentQuestionIndex) {
															posthog.capture("Friend Check Question Answered", {
																index: currentQuestionIndex,
																number: currentQuestionIndex + 1,
															});
														}

														const currentGroup = questionGroups[currentGroupIndex];

														if (currentQuestionIndex === currentGroup.length - 1) {
															setCurrentQuestionIndex(0);
															setCurrentGroupIndex((current) => ++current);
														} else {
															setCurrentQuestionIndex((current) => ++current);
														}
													}
												: undefined
										}
									>
										<AnimatePresence>
											{!isLastQuestion ? (
												<motion.span
													key="next"
													initial={{ opacity: 0, scale: 0 }}
													animate={{ opacity: 1, scale: 1 }}
													exit={{ opacity: 0, scale: 0 }}
												>
													{t("next")}
												</motion.span>
											) : (
												<motion.span
													key="send"
													initial={{ opacity: 0, scale: 0 }}
													animate={{ opacity: 1, scale: 1 }}
													exit={{ opacity: 0, scale: 0 }}
												>
													{t("send")}
												</motion.span>
											)}
										</AnimatePresence>
									</Button>
								)}
							/>
						</div>

						<div className="relative isolate flex w-fit flex-row items-center justify-center gap-2 py-2 *:size-2.5 *:rounded-full!">
							{Array.from({ length: questionGroups.length || 3 }).map((_, index) => (
								<div key={index} className="bg-brown-300" />
							))}
							<motion.div
								className="pointer-events-none absolute left-0 z-10 bg-orange-500"
								initial={{ x: "0rem" }}
								animate={{ x: `${currentGroupIndex * 1.125}rem` }}
								transition={{ type: "string", duration: 0.3 }}
							/>
						</div>
					</div>
				</IonFooter>
			</form>
		</IonPage>
	);
}
